import React, { useState, useEffect } from "react";
import {
    Input,
    Button,
    message,
    TreeSelect,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { groupCustomerAPIWithErrorHandling, GroupCustomerHelpers } from "../../../api/groupCustomer/groupCustomer";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { actionColumnDef } from "../../../components/common/RowActions";
import { confirmAction } from "../../../components/common/confirmDialog";
import GroupCustomerFormDrawer from "./GroupCustomerFormDrawer";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["name", "customers_count", "description", "period"];

export default function GroupCustomerTable() {
    const navigate = useNavigate();
    const [groupCustomers, setGroupCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 1,
    });
    const [searchText, setSearchText] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        sort_by: "created_at",
        sort_order: "desc",
    });

    const [treeData, setTreeData] = useState([]);
    const [valueOrg, setValueOrg] = useState();

    // Thêm mới / chỉnh sửa làm ngay trên danh sách bằng Drawer (chỉ có 3 trường)
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    const flattenTreeData = (nodes, parentId = 0) => {
        let result = [];
        nodes.forEach((node) => {
            result.push({
                id: node.id,
                pId: parentId,
                value: String(node.id),
                title: node.name,
                isLeaf: node.children_recursive?.length === 0,
            });

            if (node.children_recursive && node.children_recursive.length > 0) {
                result = result.concat(
                    flattenTreeData(node.children_recursive, node.id)
                );
            }
        });
        return result;
    };

    // Load Organizations for select options
    const loadOrganizations = async () => {
        try {
            const response =
                await organizationAPIWithErrorHandling.getTreeStructure();
            if (response.success) {
                const rawData = response.data;
                const flatTree = flattenTreeData(rawData);

                setTreeData(flatTree); // Dùng cho TreeSelect
            }
        } catch (error) {
            message.error(error.message);
        }
    };

    useEffect(() => {
        loadOrganizations();
        fetchGroupCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangeOrg = (newValue) => {
        setValueOrg(newValue);
        fetchGroupCustomers(1, pagination.pageSize, searchText, newValue, filters.status);
    };

    const fetchGroupCustomers = async (
        page = 1,
        perPage = 10,
        search = "",
        valueOrg = "",
    ) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: perPage,
                search: search.trim(),
                orgId: valueOrg,
                sort_by: filters.sort_by,
                sort_order: filters.sort_order,
            };

            const response =
                await groupCustomerAPIWithErrorHandling.getGroupCustomers(params);

            const { groups: groupData, pagination: paginationData } =
                GroupCustomerHelpers.extractGroupCustomerList(response);

            setGroupCustomers(groupData);
            setPagination({
                current: paginationData.current,
                pageSize: paginationData.pageSize,
                total: paginationData.total,
                from: paginationData.from,
                to: paginationData.to,
                lastPage: paginationData.lastPage,
            });
        } catch (error) {
            message.error(error.message || "Không thể tải danh sách đơn vị");
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (paginationConfig, tableFilters, sorter) => {
        if (sorter && sorter.field) {
            const sortBy = sorter.field;
            const sortOrder = sorter.order === "descend" ? "desc" : "asc";
            setFilters((prev) => ({
                ...prev,
                sort_by: sortBy,
                sort_order: sortOrder,
            }));
        }


        fetchGroupCustomers(
            paginationConfig.current,
            paginationConfig.pageSize,
            searchText,
        );
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchGroupCustomers(1, pagination.pageSize, value, valueOrg, filters.status);
    };

    const handleReset = () => {
        setSearchText("");
        setValueOrg(undefined);
        fetchGroupCustomers(1, pagination.pageSize, "", "");
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setEditingRecord(null);
    };

    const handleInfo = (record) => {
        message.info(`Chi tiết thông tin đợt thu: ${record.name}`);
        navigate(`/group-customers/information/${record.id}`);
    };

    const handleAdd = () => {
        setEditingRecord(null);
        setDrawerOpen(true);
    };

    const handleDelete = async (record) => {
        const count = record.customers_count ?? 0;

        const confirmed = await confirmAction({
            title: `Xóa nhóm "${record.name}"?`,
            content:
                count > 0
                    ? `${count} khách hàng sẽ được gỡ khỏi nhóm, không bị xóa khỏi hệ thống.`
                    : "Nhóm này chưa có khách hàng nào.",
            okText: "Xóa nhóm",
            okType: "danger",
        });
        if (!confirmed) return;

        try {
            await groupCustomerAPIWithErrorHandling.deleteGroupCustomer(record.id);
            message.success("Xóa nhóm khách hàng thành công");

            // Nếu xoá dòng cuối của trang thì lùi về trang trước để không hiện trang rỗng
            const isLastItemOnPage = groupCustomers.length === 1 && pagination.current > 1;
            fetchGroupCustomers(
                isLastItemOnPage ? pagination.current - 1 : pagination.current,
                pagination.pageSize,
                searchText,
                valueOrg
            );
        } catch (error) {
            message.error(error.message || "Không thể xóa nhóm khách hàng");
        }
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT", key: "index", width: 80,
                render: (_, __, index) =>
                    (pagination.current - 1) * pagination.pageSize + index + 1,
            },
        },
        {
            key: "code", label: "Mã nhóm", fixed: true,
            column: {
                title: "Mã nhóm", dataIndex: "code", key: "code", width: 180,
                render: (text) => <strong className="text-blue-600">{text}</strong>,
            },
        },
        {
            key: "name", label: "Tên nhóm",
            column: {
                title: "Tên nhóm", dataIndex: "name", key: "name", width: 200, sorter: true,
            },
        },
        {
            key: "description", label: "Chi tiết",
            column: {
                title: "Chi tiết", dataIndex: "description", key: "description", ellipsis: true, width: 220,
                render: (text) => text || "-",
            },
        },
        {
            key: "customers_count", label: "Số khách hàng",
            column: {
                title: "Số khách hàng", dataIndex: "customers_count", key: "customers_count",
                width: 130, align: "center",
                render: (value) => value ?? 0,
            },
        },
        {
            key: "period", label: "Đơn vị",
            column: {
                title: "Đơn vị", key: "period", width: 180,
                render: (_, record) => (
                    <div>
                        {record.organization?.name}
                    </div>
                ),
            },
        },
        actionColumnDef((record) => [
            { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
            { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => handleEdit(record) },
            { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
        ]),
    ];

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Input.Search
                    placeholder="Tìm kiếm theo tên, mã,..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    style={{ width: "100%" }}
                    enterButton={<SearchOutlined />}
                    allowClear
                />
            ),
        },
        {
            key: "organization", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <TreeSelect
                    treeDataSimpleMode
                    style={{ width: "100%" }}
                    value={valueOrg}
                    styles={{
                        popup: { root: { maxHeight: 400, overflow: "auto" } },
                    }}
                    placeholder="Chọn đơn vị"
                    onChange={onChangeOrg}
                    treeData={treeData}
                    allowClear
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = !!(searchText || valueOrg);

    return (
        <BrandedPageLayout
            title="Nhóm khách hàng"
            subtitle="Quản lý danh sách nhóm khách hàng theo đơn vị."
            breadcrumbItems={[{ title: "Nhóm khách hàng" }, { title: "Danh sách" }]}
        >
            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleReset}
                onReload={() => fetchGroupCustomers(pagination.current, pagination.pageSize, searchText, valueOrg)}
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Thêm nhóm khách hàng
                    </Button>
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={groupCustomers}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có dữ liệu"
            />

            <GroupCustomerFormDrawer
                open={drawerOpen}
                record={editingRecord}
                treeData={treeData}
                onClose={handleCloseDrawer}
                onSaved={() =>
                    fetchGroupCustomers(
                        editingRecord ? pagination.current : 1,
                        pagination.pageSize,
                        searchText,
                        valueOrg
                    )
                }
            />
        </BrandedPageLayout>
    );
}
